import { useState, useEffect } from 'react';
import { CONSENT_SAVED_EVENT } from '@/utils/talentSplash';
import { motion, AnimatePresence } from 'motion/react';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';
import { X, Cookie, Shield, BarChart3, Target, Settings, ChevronUp } from 'lucide-react';
import { setAnalyticsConsent, setMarketingConsent } from '../utils/analytics';
import { forgetPersistentAttribution } from '../utils/attribution';

interface CookiePreferences {
  necessary: boolean;
  functional: boolean;
  analytics: boolean;
  marketing: boolean;
}

const defaultPreferences: CookiePreferences = {
  necessary: true, // Always required
  functional: false,
  analytics: false,
  marketing: false,
};

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    const openPreferences = () => {
      try {
        const stored = JSON.parse(localStorage.getItem('weareqa-cookie-consent') || '{}').preferences;
        setPreferences({
          necessary: true,
          functional: Boolean(stored?.functional),
          analytics: Boolean(stored?.analytics),
          marketing: Boolean(stored?.marketing),
        });
      } catch {
        setPreferences(defaultPreferences);
      }
      setShowDetails(true);
      setIsVisible(true);
    };
    window.addEventListener('waq:cookie-preferences', openPreferences);
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Check if user has already made a choice
    const consent = localStorage.getItem('weareqa-cookie-consent');
    if (!consent) {
      // Show banner after a small delay for better UX
      timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
    }
    return () => {
      clearTimeout(timer);
      window.removeEventListener('waq:cookie-preferences', openPreferences);
    };
  }, []);

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true,
    };
    
    localStorage.setItem('weareqa-cookie-consent', JSON.stringify({
      preferences: allAccepted,
      timestamp: new Date().toISOString(),
      version: '1.0'
    }));
    
    setIsVisible(false);
    applyConsent(allAccepted);
  };

  const handleAcceptSelected = () => {
    localStorage.setItem('weareqa-cookie-consent', JSON.stringify({
      preferences,
      timestamp: new Date().toISOString(),
      version: '1.0'
    }));
    
    setIsVisible(false);
    applyConsent(preferences);
  };

  const handleDeclineAll = () => {
    const necessaryOnly: CookiePreferences = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false,
    };
    
    localStorage.setItem('weareqa-cookie-consent', JSON.stringify({
      preferences: necessaryOnly,
      timestamp: new Date().toISOString(),
      version: '1.0'
    }));
    
    setIsVisible(false);
    applyConsent(necessaryOnly);
  };

  /*
   * Hand the visitor's answer to the tracking tools. Called for every outcome,
   * including "decline all" — a denial has to be forwarded too, otherwise
   * revoking an earlier consent would leave the tag measuring until a reload.
   */
  const applyConsent = (prefs: CookiePreferences) => {
    // Anything waiting on an answer (the home page's QA Talent splash) hears it here.
    window.dispatchEvent(new Event(CONSENT_SAVED_EVENT));
    // GA4, loaded in index.html with every storage type denied until now.
    setAnalyticsConsent(prefs.analytics);
    setMarketingConsent(prefs.marketing);
    // The 90-day campaign copy exists only with marketing consent; without it, it goes now.
    if (!prefs.marketing) forgetPersistentAttribution();

    if (prefs.functional) {
      // Initialize functional cookies like chat widgets, etc.
    }
  };

  const updatePreference = (type: keyof CookiePreferences, value: boolean) => {
    if (type === 'necessary') return; // Necessary cookies cannot be disabled
    
    setPreferences(prev => ({
      ...prev,
      [type]: value
    }));
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <div className="fixed bottom-0 left-0 right-0 z-50 pointer-events-auto pb-[env(safe-area-inset-bottom)]">
          <motion.div 
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ 
              type: "spring",
              stiffness: 300,
              damping: 30,
              duration: 0.4
            }}
            className="max-w-6xl mx-auto p-3 sm:p-4"
          >
            <div className="relative bg-white/95 backdrop-blur-xl border border-qa-blue-200/50 rounded-2xl shadow-2xl overflow-hidden">
              
              {/* Subtle animated gradient border */}
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-qa-blue-500/10 via-qa-yellow-400/10 to-qa-blue-500/10 p-[1px]">
                <div className="w-full h-full bg-white/95 backdrop-blur-xl rounded-2xl" />
              </div>
              
              <div className="relative p-4 sm:p-6">
                {/* Header with compact styling */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-xl bg-qa-blue-600 flex items-center justify-center shadow-lg">
                      <Cookie className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-qa-blue-900 mb-1">
                        Privacy & Cookies
                      </h3>
                      <p className="text-sm text-qa-blue-700">
                        Your privacy is important to us
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsVisible(false)}
                    aria-label="Close cookie preferences"
                    className="w-8 h-8 rounded-lg bg-qa-blue-100 hover:bg-qa-blue-200 border border-qa-blue-200 flex items-center justify-center text-qa-blue-600 hover:text-qa-blue-700 transition-all duration-200 cursor-pointer"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* Main Content */}
                <div className="space-y-4">
                  <p className="text-qa-blue-800 leading-relaxed">
                    We use cookies to enhance your experience, provide personalized content, and analyze our traffic. 
                    Choose your preferences or accept all to continue with the recommended experience.
                  </p>

                  {/* Cookie Details with light theme */}
                  <AnimatePresence>
                    {showDetails && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <Separator className="bg-qa-blue-200 mb-4" />
                        <div className="space-y-3">
                          <h4 className="text-lg font-semibold text-qa-blue-900 flex items-center space-x-2">
                            <Settings className="h-4 w-4 text-qa-blue-600" />
                            <span>Cookie Categories</span>
                          </h4>
                          
                          {/* Cookie category cards with light styling */}
                          {[
                            {
                              key: 'necessary',
                              icon: Shield,
                              title: 'Necessary Cookies',
                              description: 'Essential for the website to function properly. Cannot be disabled.',
                              required: true,
                              checked: preferences.necessary,
                              color: 'from-emerald-500 to-emerald-600'
                            },
                            {
                              key: 'functional',
                              icon: Cookie,
                              title: 'Functional Cookies',
                              description: 'Enable enhanced functionality like chat widgets and preferences.',
                              required: false,
                              checked: preferences.functional,
                              color: 'from-blue-500 to-blue-600'
                            },
                            {
                              key: 'analytics',
                              icon: BarChart3,
                              title: 'Analytics Cookies',
                              description: 'Help us understand how visitors use our website to improve user experience.',
                              required: false,
                              checked: preferences.analytics,
                              color: 'from-purple-500 to-purple-600'
                            },
                            {
                              key: 'marketing',
                              icon: Target,
                              title: 'Marketing Cookies',
                              description: 'Used to deliver relevant ads and measure the effectiveness of campaigns.',
                              required: false,
                              checked: preferences.marketing,
                              color: 'from-orange-500 to-orange-600'
                            }
                          ].map((cookie) => (
                            <div
                              key={cookie.key}
                              className="group p-3 rounded-xl bg-qa-blue-50/50 backdrop-blur-sm border border-qa-blue-200/50 hover:border-qa-blue-300/50 transition-all duration-300"
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3">
                                  <div className={`w-8 h-8 bg-gradient-to-br ${cookie.color} rounded-lg flex items-center justify-center shadow-sm`}>
                                    <cookie.icon className="h-4 w-4 text-white" />
                                  </div>
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2">
                                      <span className="font-semibold text-qa-blue-900 text-sm">{cookie.title}</span>
                                      {cookie.required && (
                                        <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full border border-emerald-200">
                                          Required
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-qa-blue-700 mt-0.5">
                                      {cookie.description}
                                    </p>
                                  </div>
                                </div>
                                <Switch
                                  checked={cookie.checked}
                                  disabled={cookie.required}
                                  onCheckedChange={(checked) => !cookie.required && updatePreference(cookie.key as keyof CookiePreferences, checked)}
                                  className="cookie-switch"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <div className="flex-1 flex flex-col sm:flex-row gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowDetails(!showDetails)}
                        className="flex-1 bg-qa-blue-50 border-qa-blue-300 text-qa-blue-800 hover:bg-qa-blue-100 hover:border-qa-blue-400 transition-all duration-200 text-sm"
                      >
                        <Settings className="h-3 w-3 mr-2" />
                        {showDetails ? (
                          <span><ChevronUp className="h-3 w-3 mr-1" />Hide Details</span>
                        ) : (
                          'Customize Settings'
                        )}
                      </Button>
                      
                      {/*
                        On the first layer, next to Accept All. It used to appear
                        only after "Customize Settings" was opened, which made
                        refusing take two clicks and accepting one. European
                        guidance (EDPB, CNIL) treats that asymmetry as consent
                        that was not freely given -- and Google Ads' EU user
                        consent policy relies on the consent being valid, so an
                        invalid banner undermines every ad_* signal it sends.
                      */}
                      <Button
                        type="button"
                        onClick={handleDeclineAll}
                        className="flex-1 bg-red-100 hover:bg-red-200 text-red-700 font-semibold border-0 shadow-lg hover:shadow-red-200/25 transition-all duration-200 text-sm"
                      >
                        Necessary Only
                      </Button>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-2">
                      {showDetails && (
                        <Button
                          type="button"
                          onClick={handleAcceptSelected}
                          className="w-full sm:w-auto bg-qa-blue-600 hover:bg-qa-blue-700 text-white px-6 shadow-lg hover:shadow-qa-blue-500/25 transition-all duration-200 text-sm"
                        >
                          Save Preferences
                        </Button>
                      )}
                      
                      <Button
                        type="button"
                        onClick={handleAcceptAll}
                        className="w-full sm:w-auto bg-qa-yellow-400 hover:bg-qa-yellow-500 text-qa-blue-900 font-semibold px-6 shadow-lg hover:shadow-qa-yellow-400/25 transition-all duration-200 text-sm border-0"
                      >
                        Accept All Cookies
                      </Button>
                    </div>
                  </div>

                  {/* Additional Info */}
                  <div className="text-center pt-2">
                    <p className="text-xs text-qa-blue-600">
                      You can change your preferences anytime in our{' '}
                      <button 
                        type="button"
                        onClick={() => window.open('/privacy-policy', '_blank')}
                        className="text-qa-blue-700 hover:text-qa-blue-800 underline underline-offset-2 transition-colors cursor-pointer hover:cursor-pointer"
                      >
                        Privacy Policy
                      </button>
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
